/* webpackChunkName: "HeaderFooter" */
import React, { startTransition, useEffect, useMemo } from 'react';
import axios from 'axios';
import { onAxiosRequest, onAxiosRequestError } from 'utility/axios-interceptors';
import useClientSideRender from '../../hooks/useClientSideRender';
import {
  breakpointSelector,
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from '../../store/selectors/commonSelector';
import { getViewPortBreakpoint } from '../../utility/common-utils';
import {
  setFooterTaxo,
  setHeaderTaxo,
  setProgressiveHeaderTaxo,
} from '../../store/slices/taxonomy';
import { ReduxStore, useAppDispatch, useAppSelector } from '../../store/store';

import FooterRouter from '../footer/FooterRouter';
import HeaderRouter from '../header/HeaderRouter';
import { StoreIdentifierName } from '../../types';
import { setBreakpoint } from '../../store/slices/common';
import useHomr from '../../hooks/useHomr';
import useWindowVariables from '../../hooks/useWindowVariables';

type Props = {
  route: 'header' | 'footer';
  store: ReduxStore;
};

/*
  HeaderFooter is a shared entry point component prior to routing to Header or Footer component templates.
  This component should be used to put logic or components that is shared/used for all Header & Footer templates.
*/

const HeaderFooter = ({ route, store }: Props) => {
  // Set up axios interceptors to forward incoming server headers to all requests
  axios.interceptors.request.use(
    (config) => onAxiosRequest(config, store) as typeof config,
    onAxiosRequestError,
  );

  const dispatch = useAppDispatch();
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  useWindowVariables();
  const { isClient } = useClientSideRender();

  const progressiveHeaderDesktopEnabled = useAppSelector(
    getFeatureFlagByNameSelector('progressiveTaxoDesktop'),
  );

  // determine which taxo to fetch and set in the redux state
  const routeToFetch = useMemo(() => {
    if (route === 'header' && progressiveHeaderDesktopEnabled) {
      return 'progressive-header';
    }
    if (route === 'header') {
      return 'header';
    }
    return 'footer';
  }, [route, progressiveHeaderDesktopEnabled]);
  const setter = useMemo(() => {
    switch (routeToFetch) {
      case 'footer':
        return setFooterTaxo;
      case 'progressive-header':
        return setProgressiveHeaderTaxo;
      default:
        return setHeaderTaxo;
    }
  }, [routeToFetch]);
  useHomr(routeToFetch, 'default', storeIdentifierName as StoreIdentifierName, setter);

  const updateViewPortBreakpoint = () => {
    const breakpointValue = getViewPortBreakpoint();
    if (viewPortBreakpoint !== breakpointValue) {
      startTransition(() => {
        dispatch(setBreakpoint(breakpointValue));
      });
    }
  };

  useEffect(() => {
    // Handles listening for browser resizes and updating our global breakpoint value for dynamic rendering
    if (isClient) {
      updateViewPortBreakpoint();
    }
    window.addEventListener('resize', updateViewPortBreakpoint);
    return () => {
      window.removeEventListener('resize', updateViewPortBreakpoint);
    };
  }, [isClient, viewPortBreakpoint]);

  return (
    <>
      {route === 'footer' ? (
        <FooterRouter key="footer" storeIdentifierName={storeIdentifierName} />
      ) : (
        <HeaderRouter key="header" storeIdentifierName={storeIdentifierName} />
      )}
    </>
  );
};

export default HeaderFooter;
